/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: branding-media-edit-view-component.ts
 *
 * Created by rwilliams on 8/6/17,
 * Refactored by kyang on 10/20/20.
 */

function BrandingMediaEditViewController(
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    $location: JS5Object,
    $route: JS5Object,
    $routeParams: JS5Object,
    BrandingAssetEditService: JS5Object,
    BrandingAssetService: JS5Object,
    BrandingViewService: JS5Object,
    BreadcrumbService: JS5Object,
    LanguagesService: JS5Object,
    RatingsService: JS5Object,
    RolesService: JS5Object,
    SidebarService: JS5Object,
    TerritoriesService: JS5Object,
) {

    const hasBrandingAssetContainer = () => {
        return this.brandingConfig.type !== 'ratingOverlays';
    };

    const getContainerLocation = () => {
        let basePath = this.brandingConfig.parentLocation;
        if (this.brandingConfig.groupName === 'networks') {
            basePath +=  '/' + $route.current.params.type;
        }
        return `${basePath}/edit/${this.brandingAssetContainer.id}`;
    };

    const editingNewBrandingAsset = () => {
        return !_.get(this, 'originalEditData.id');
    };


    //===================//
    // Template Response //
    //===================//

    this.addAudio = () => {
        this.rootAsset.audios.push({});
        this.editorForm.$setDirty();
    };

    this.removeAudio = (audioIndex: number) => {
        this.rootAsset.audios.splice(audioIndex, 1);
        this.editorForm.$setDirty();
    };

    this.addText = () => {
        this.rootAsset.texts.push({});
        this.editorForm.$setDirty();
    };

    this.removeText = (textIndex: number) => {
        this.rootAsset.texts.splice(textIndex, 1);
        this.editorForm.$setDirty();
    };

    this.addArtwork = () => {
        this.rootAsset.assets.push({
            acceptProfile: this.rootAsset.acceptProfile,
            type: this.rootAsset.type,
        });
        this.editorForm.$setDirty();
    };

    this.removeArtwork = (artIndex: number) => {
        this.rootAsset.assets.splice(artIndex, 1);
        this.editorForm.$setDirty();
    };

    this.addOverlayImage = () => {
        this.rootAsset.images.push({
            mediaAcceptProfile: this.rootAsset.mediaAcceptProfile,
        });
        this.editorForm.$setDirty();
    };

    this.removeOverlayImage = (imageIndex: number) => {
        this.rootAsset.images.splice(imageIndex, 1);
        this.editorForm.$setDirty();
    };


    //=========//
    // Toolbar //
    //=========//

    this.toolbarButtonDefs = [
        {
            label: 'Save',
            enabled: () => this.canWriteMedia && !this.editorForm.$pristine && this.editorForm.$valid,
            onClick: () => {
                let savePromise: JS5Promise;
                this.loadingIndicator.show = true;
                if (editingNewBrandingAsset()) {
                    savePromise = BrandingAssetService.createRootBrandingAsset(this.brandingConfig, this.rootAsset);
                } else {
                    savePromise = BrandingAssetService.updateRootBrandingAsset(this.brandingConfig, this.rootAsset);
                }
                savePromise.then(() => {
                    return getData();
                }).then(() => {
                    if (BrandingAssetService.hasVideoAssetProxyStatus(this.brandingConfig, this.rootAsset)) {
                        BrandingViewService.createRootAssetProxyNotifications(this.brandingConfig, this.rootAsset)
                            .finally(getData);
                    }
                }).finally(() => {
                    this.loadingIndicator.show = false;
                });
            }
        }, {
            label: 'Revert',
            enabled: () => !this.editorForm.$pristine,
            onClick: () => {
                this.rootAsset = _.cloneDeep(this.originalEditData);
                this.selectedRatingSystems = _.cloneDeep(this.originalSelectedRatingSystems);
                this.editorForm.$setPristine();
            }
        }, {
            label: 'Close',
            enabled: () => this.editorForm.$pristine,
            onClick: () => {
                if (hasBrandingAssetContainer()) {
                    $location.path(getContainerLocation());
                } else {
                    $location.path(this.brandingConfig.parentLocation);
                    $location.search(`view=${this.brandingConfig.type}`);
                }
            }
        }
    ];


    //=================//
    // Lifecycle Hooks //
    //=================//

    const setBreadcrumbs = () => {
        if (!this.breadcrumbName) {
            return BrandingViewService.initializeParentBreadcrumbs(this.brandingConfig, $route.current.params)
                .then(() => {
                    if (hasBrandingAssetContainer()) {
                        BreadcrumbService.addBreadcrumb({
                            name: this.brandingAssetContainer.name,
                            location: getContainerLocation(),
                        });
                    }
                    this.breadcrumbName = this.mediaAcceptProfile.name;
                });
        }
    };

    const getData = () => {
        this.loadingIndicator.show = true;

        return $q.all({
            container: BrandingAssetService.getBrandingAssetContainer(this.brandingConfig),
            audioRoles: RolesService.getTypedRoles('audio'),
            ccRoles: RolesService.getTypedRoles('cc'),
            languages: LanguagesService.getAllLanguages(),
            mediaAcceptProfiles: BrandingViewService.getMediaAcceptProfiles(),
            ratingSystems: RatingsService.getRatingSystems(),
            territories: TerritoriesService.getAllTerritories({fetchLimit: 0, includeHidden: false}),
        }).then((data: JS5Object) => {
            this.brandingAssetContainer = data.container;
            this.mediaAcceptProfile = data.mediaAcceptProfiles.find((acceptProfile: JS5Object) => {
                return acceptProfile.id === $routeParams.acceptProfileId;
            });
            this.editorForm.$setPristine();
            this.originalEditData = BrandingAssetService.getRootBrandingAsset(this.brandingConfig, this.brandingAssetContainer, this.mediaAcceptProfile);
            if (this.canReadMedia) {
                this.rootAsset = _.cloneDeep(this.originalEditData);
                this.availableAudioRoles = _.sortBy(data.audioRoles, 'name');
                this.availableLanguages = _.chain(data.languages)
                    .orderBy('name')
                    .partition({isDefault: true})
                    .flatten()
                    .value();
                if (this.brandingConfig.groupName === 'networks') {
                    this.availableRatingSystems = _.values(data.ratingSystems);
                    this.selectedRatingSystems = _.map(this.rootAsset.images, (image: JS5Object) => {
                        return RatingsService.getRatingSystemByRatingCode(data.ratingSystems, image.rating.ratingCode);
                    });
                    this.originalSelectedRatingSystems = _.cloneDeep(this.selectedRatingSystems);
                }
                this.availableTerritories = _.chain(data.territories)
                    .orderBy('name')
                    .partition({name: 'USA'})
                    .flatten()
                    .value();
                this.availableTextRoles = _.sortBy(data.ccRoles, 'name');

                data.mediaAcceptProfiles = _.sortBy(data.mediaAcceptProfiles, 'name');
                this.availableArtworkAcceptProfiles = _.filter(data.mediaAcceptProfiles, {type: 'OTHER'});
                this.availableAudioAcceptProfiles = _.filter(data.mediaAcceptProfiles, {type: 'AUDIO'});
                this.availableImageAcceptProfiles = _.filter(data.mediaAcceptProfiles, {type: 'IMAGE'});
                this.availableArtworkLanguages = [{
                    id: -1,
                    name: '[None]'
                }];
                this.availableArtworkLanguages.push(...data.languages);
            }
            setBreadcrumbs();
        }).finally(() => {
            this.loadingIndicator.show = false;
        });
    };

    this.$onInit = () => {
        this.breadcrumbName = '';
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        this.loadingIndicator.show = true;
        BrandingViewService.getReadWritePermissions(this.brandingConfig)
            .then((permissions: JS5Object) => {
                this.canReadMedia = permissions.read;
                this.canWriteMedia = permissions.write;
                return getData();
            });
    }

    this.$postLink = () => {
        BrandingAssetEditService.editorForm = this.editorForm;
        BreadcrumbService.editService = BrandingAssetEditService;
        SidebarService.editService = BrandingAssetEditService;
    };

    this.$onDestroy = () => {
        BrandingAssetEditService.editorForm = null;
        BreadcrumbService.clearBreadcrumbs();
    };
}

angular
    .module('prism-ui.components.branding')
    .component('brandingMediaEditView', {
        templateUrl: 'components/branding/editView/components/media/editView/branding-media-edit-view-template.html',
        controller: BrandingMediaEditViewController,
        bindings: {
            brandingConfig: '<'
        }
    });
